// features/tutorials/TutorialsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TutorialHero } from './components/TutorialHero';
import { ProgressOverview } from './components/ProgressOverview';
import { ModuleGrid } from './components/ModuleGrid';
import { LessonView } from './components/LessonView';
import { SearchOverlay } from './components/SearchOverlay';
import { CompletionCelebration } from './components/CompletionCelebration';
import { AnimatedBackground } from './components/AnimatedBackground';
import { useLearningProgress } from './hooks/useLearningProgress';
import { tutorialModules } from './data/tutorialContent';
import type { TutorialModule, TutorialLesson } from './types';

export const TutorialsPage: React.FC = () => {
    const [selectedModule, setSelectedModule] = useState<TutorialModule | null>(null);
    const [selectedLesson, setSelectedLesson] = useState<TutorialLesson | null>(null);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);
    const [expandedModuleId, setExpandedModuleId] = useState<string | null>(null);

    const {
        progress,
        completeLesson,
        completeModule,
        isLessonCompleted,
        isModuleCompleted,
        getModuleProgress,
        getOverallProgress,
        getResumeLearning,
    } = useLearningProgress();

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyboard = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsSearchOpen(true);
            }
            if (e.key === 'Escape') {
                if (selectedLesson) {
                    setSelectedLesson(null);
                } else if (isSearchOpen) {
                    setIsSearchOpen(false);
                } else {
                    setExpandedModuleId(null);
                }
            }
        };

        window.addEventListener('keydown', handleKeyboard);
        return () => window.removeEventListener('keydown', handleKeyboard);
    }, [selectedLesson, isSearchOpen]);

    const handleModuleClick = (module: TutorialModule) => {
        setExpandedModuleId(expandedModuleId === module.id ? null : module.id);
        setSelectedModule(module);
    };

    const handleLessonClick = (lesson: TutorialLesson) => {
        setSelectedLesson(lesson);
        setExpandedModuleId(null);
    };

    const handleLessonComplete = useCallback((lessonId: string) => {
        completeLesson(lessonId);

        // Check if module is complete
        const currentModule = tutorialModules.find((m: TutorialModule) =>
            m.lessons.some((l: TutorialLesson) => l.id === lessonId)
        );

        if (currentModule && currentModule.lessons.every((l: TutorialLesson) =>
            isLessonCompleted(l.id) || l.id === lessonId
        )) {
            completeModule(currentModule.id);
            setShowCelebration(true);
            setTimeout(() => setShowCelebration(false), 4000);
        }
    }, [completeLesson, completeModule, isLessonCompleted]);

    const handleCloseLesson = () => {
        setSelectedLesson(null);
    };

    const resumeLearning = getResumeLearning();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
            <AnimatedBackground />

            <AnimatePresence>
                {showCelebration && (
                    <CompletionCelebration
                        message="Module Completed! 🎉"
                        submessage="Amazing progress! Keep up the great work!"
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isSearchOpen && (
                    <SearchOverlay
                        modules={tutorialModules}
                        onClose={() => setIsSearchOpen(false)}
                        onLessonSelect={(lesson: TutorialLesson) => {
                            setSelectedLesson(lesson);
                            setIsSearchOpen(false);
                        }}
                    />
                )}
            </AnimatePresence>

            <div className="relative z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <AnimatePresence mode="wait">
                        {!selectedLesson ? (
                            <motion.div
                                key="main"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <TutorialHero
                                    onSearchFocus={() => setIsSearchOpen(true)}
                                    overallProgress={getOverallProgress()}
                                    resumeLearning={resumeLearning}
                                    onResumeLearning={(lesson: TutorialLesson) => setSelectedLesson(lesson)}
                                />

                                <ProgressOverview
                                    progress={progress}
                                    modules={tutorialModules}
                                />

                                <ModuleGrid
                                    modules={tutorialModules}
                                    onModuleClick={handleModuleClick}
                                    onLessonClick={handleLessonClick}
                                    expandedModuleId={expandedModuleId}
                                    getModuleProgress={getModuleProgress}
                                    isLessonCompleted={isLessonCompleted}
                                    isModuleCompleted={isModuleCompleted}
                                />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="lesson"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <LessonView
                                    lesson={selectedLesson}
                                    onClose={handleCloseLesson}
                                    onComplete={handleLessonComplete}
                                    progress={progress}
                                    modules={tutorialModules}
                                    onUpdateProgress={(updatedProgress) => {
                                        localStorage.setItem('etipos_learning_progress', JSON.stringify(updatedProgress));
                                    }}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default TutorialsPage;